import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Blocking first-login password change for company managers / employees.
 */
const ForceChangePasswordModal = ({ endpoint, onSuccess }) => {
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.current_password || !form.new_password || !form.confirm_password) {
      setError('Please fill in all fields');
      return;
    }

    if (form.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (form.current_password === form.new_password) {
      setError('New password must be different from your temporary password');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_URL}${endpoint}`, {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center px-4">
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-border"
        role="dialog"
        aria-modal="true"
        aria-labelledby="force-change-password-title"
      >
        <h2 id="force-change-password-title" className="text-xl font-semibold text-ink">
          Change your password
        </h2>
        <p className="text-sm text-ink-secondary mt-2">
          For security, you must set a new password before using your dashboard. Use the temporary
          password from your activation email as the current password.
        </p>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-100 text-red-700 px-3 py-2 rounded-xl text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-label mb-1.5">
              Current / temporary password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={form.current_password}
              onChange={(e) => setForm((prev) => ({ ...prev, current_password: e.target.value }))}
              className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-label mb-1.5">New password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={form.new_password}
              onChange={(e) => setForm((prev) => ({ ...prev, new_password: e.target.value }))}
              className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Min. 6 characters"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-label mb-1.5">
              Confirm new password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={form.confirm_password}
              onChange={(e) => setForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
              className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            Show passwords
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 rounded-chip disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Save new password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForceChangePasswordModal;
